// ============================================================
// THE PSYKER ON THE PLATE (plan body-map T1; the bench's psyker, ruled
// 2026-10-09). No trait: the sheet's Psy Rating (1-10) is the degree, and
// every step adds to the last -
//
//   1+   a ring of Voynich runes rising round the feet, each a thin slab
//        turning in space and dissolving at the top, over a light that
//        wells up inside the ring; embers rise off the ring, ash falls off
//        the runes; a gravity well draws grit in, ripples run out
//   3+   the body lit from within, motes rising up it
//   4+   an arc leaps between the runes, more often the higher
//   8+   debris lifts inside the ring
//   9+   a crown of glyphs turns above the head
//   10   a pillar of pale light to the top of the plate
//
// The runes, the crown and the arc are sampled per frame (the plate's clock
// writes them); the particles and the ground are looping tracks out of step
// with each other (./tracks), as the bench's negative SMIL begins were. The
// bench's Math.random arc is a seeded choice per sixtieth of a second.
//
// Pure. Zero React dependency. Frame units; seconds.
// ============================================================

import type { Box } from './frame';
import { hash01 } from './hash';
import { frac, loop, type Track } from './tracks';

/** The floor the ring stands on: its centre, the ground line under the feet, its radii (foreshortened). */
export interface PsyFloor {
  cx: number;
  gy: number;
  rx: number;
  ry: number;
}

/** The ring's floor under a figure's whole bounds - never narrower than the bench's 560. */
export function psyFloor([x0, , x1, y1]: Box): PsyFloor {
  const rx = Math.max(560, (x1 - x0) * 0.55);
  return { cx: (x0 + x1) / 2, gy: y1 + 30, rx, ry: rx * 0.16 };
}

export const runeCount = (r: number) => 4 + 2 * r;

/** How the ring moves at a rating: how high, how fast round and up, how big, how bright. */
const pace = (r: number) => ({
  n: runeCount(r),
  H: 700 + 230 * r,
  orbit: 0.12 + 0.05 * r,
  climb: 1 / (6.5 - 0.4 * r),
  size: 0.75 + 0.05 * r,
  light: 0.25 + 0.075 * r,
});

const fixed = (n: number, d = 1) => Number(n.toFixed(d));

/** The ring, the glow inside it and the column of light above, pulsing. */
export function psyLight(f: PsyFloor, r: number, t: number) {
  const p = pace(r);
  const light = p.light * (0.85 + 0.15 * Math.sin(t * (1.5 + 0.3 * r)));
  return {
    ring: { cx: f.cx, cy: f.gy, rx: f.rx, ry: f.ry, opacity: fixed(light, 2) },
    glow: { cx: f.cx, cy: f.gy, rx: f.rx * 1.2, ry: f.ry * 2.2, opacity: fixed(light, 2) },
    column: { x: f.cx - f.rx * 0.75, y: f.gy - p.H, width: f.rx * 1.5, height: p.H, opacity: fixed(0.18 * light, 3) },
  };
}

/** One rune at a moment: where, how much of it faces us, how bright, its strokes (broken near the top), its slabs. */
export interface RuneAt {
  x: number;
  y: number;
  /** -1 the back of the ring, 1 the front. */
  front: number;
  opacity: number;
  dash: string;
  /** Its three stroked layers' transforms: as it turns, the back layers slide out along its edge. */
  slabs: string[];
}

/** In over the first eighth of its climb, out over the last third. */
const climbFade = (ph: number) => (ph < 0.12 ? ph / 0.12 : ph > 0.7 ? 1 - (ph - 0.7) / 0.3 : 1);

/** Rune `k` of the ring at time `t`: orbiting, climbing its own cycle, turning; the back dimmer and smaller. */
export function runeAt(k: number, f: PsyFloor, r: number, t: number): RuneAt {
  const p = pace(r);
  const a = t * p.orbit + (k / p.n) * Math.PI * 2;
  const ph = frac(t * p.climb + frac(k * 0.618));
  const front = Math.sin(a);
  const x = f.cx + f.rx * Math.cos(a);
  const y = f.gy + f.ry * front - p.H * ph;
  return {
    x,
    y,
    front,
    opacity: fixed(climbFade(ph) * (0.35 + (0.65 * (front + 1)) / 2), 2),
    dash: ph > 0.62 ? `${fixed(12 * (1 - ph))} ${fixed(40 * (ph - 0.62))}` : 'none',
    slabs: slabsOf({ x, y, scale: (0.6 + (0.4 * (front + 1)) / 2) * p.size }, k + t * (1.2 + 0.25 * r), t * 0.7 + k),
  };
}

/**
 * A rune's three layers at a place and size: turned by `spin` about its upright and tilted by `tilt`, the back two
 * offset along its edge by the spin - the slab's thickness.
 */
function slabsOf({ x, y, scale }: { x: number; y: number; scale: number }, spin: number, tilt: number): string[] {
  const c = Math.cos(spin);
  const sx = Math.sign(c || 1) * Math.max(0.08, Math.abs(c)) * scale;
  const sy = Math.max(0.3, Math.cos(0.35 * Math.sin(tilt))) * scale;
  const skew = fixed(12 * Math.sin(spin));
  return [0, 1, 2].map((j) => {
    const off = (2 - j) * 9 * Math.sin(spin) * scale;
    return `translate(${fixed(x + off)} ${fixed(y)}) scale(${fixed(sx, 3)} ${fixed(sy, 3)}) skewY(${skew})`;
  });
}

/** Glyph `k` of the crown (from 9) above the head of a figure with these bounds: turning, its back dimmer. */
export function crownAt(k: number, [x0, y0, x1]: Box, t: number): { transform: string; opacity: number } {
  const R = (x1 - x0) * 0.42;
  const a = t * 0.5 + (k / 10) * Math.PI * 2;
  const front = Math.sin(a);
  const sc = 0.45 * (0.7 + (0.3 * (front + 1)) / 2);
  const x = (x0 + x1) / 2 + R * Math.cos(a);
  const y = y0 - 150 + R * 0.22 * front;
  const sx = sc * Math.max(0.15, Math.abs(Math.cos(a * 2 + t)));
  return {
    transform: `translate(${fixed(x)} ${fixed(y)}) scale(${fixed(sx, 3)} ${fixed(sc, 3)})`,
    opacity: fixed(0.45 + (0.55 * (front + 1)) / 2, 2),
  };
}

/** The arc: each sixtieth of a second may strike (8% a step above 3), then fade over six. */
const ARC = { slot: 1 / 60, peak: 0.9, fade: 0.15, legs: 6, jitter: 90 };
export const NO_ARC = { d: '', opacity: 0 };

/** The arc between two front runes at `t` - struck in the latest slot that fired, fading since - or none. */
export function arcAt(t: number, r: number, runes: readonly RuneAt[]): { d: string; opacity: number } {
  if (r < 4) return NO_ARC;
  const slot = Math.floor(t / ARC.slot);
  for (let back = 0; back < ARC.legs; back++) {
    if (hash01(slot - back, 7.31) < 0.08 * (r - 3)) return arcPath(slot - back, runes, ARC.peak - ARC.fade * back);
  }
  return NO_ARC;
}

/** The slot's arc: two front runes its seed picks, a jagged line between them. */
function arcPath(slot: number, runes: readonly RuneAt[], opacity: number): { d: string; opacity: number } {
  const front = runes.filter((q) => q.front > 0);
  const [p, q] = [
    front[Math.floor(hash01(slot, 1.3) * front.length)],
    front[Math.floor(hash01(slot, 2.9) * front.length)],
  ];
  if (!p || !q || p === q) return NO_ARC;
  const jitter = (i: number, axis: number) => (i < ARC.legs ? (hash01(slot + i, axis) - 0.5) * ARC.jitter : 0);
  const legs = Array.from({ length: ARC.legs }, (_, n) => {
    const i = n + 1;
    const x = p.x + ((q.x - p.x) * i) / ARC.legs + jitter(i, 4.7);
    return `L${fixed(x)} ${fixed(p.y + ((q.y - p.y) * i) / ARC.legs + jitter(i, 5.9))}`;
  });
  return { d: `M${fixed(p.x)} ${fixed(p.y)} ${legs.join(' ')}`, opacity: fixed(opacity, 2) };
}

/** A particle's looping tracks: each attribute it moves, all on one cycle and phase. */
type Loop<K extends string> = Record<K, Track>;

/** Embers rising off the ring (4 + 3 a rating), drifting, flaring; in front of the figure on the near side. */
export function embers(f: PsyFloor, r: number): (Loop<'cx' | 'cy' | 'opacity'> & { r: number; front: boolean })[] {
  return Array.from({ length: 4 + 3 * r }, (_, k) => {
    const a = k * 2.399;
    const [x, y] = [f.cx + f.rx * Math.cos(a), f.gy + f.ry * Math.sin(a)];
    const dur = 3.6 - 0.15 * r + (k % 3) * 0.4;
    const offset = (k * 0.37) % 3;
    const rise = (500 + 150 * r) * (0.6 + 0.4 * frac(k * 0.618));
    return {
      cx: loop('cx', [x, x + 30, x - 20, x + 10], dur, { offset }),
      cy: loop('cy', [y, y - rise], dur, { offset }),
      opacity: loop('opacity', [0, 1, 0.8, 0], dur, { offset }),
      r: 7 + (k % 3) * 3,
      front: Math.sin(a) > 0,
    };
  });
}

/** One flake of rune ash (or one lifted shard): where it starts, its cycle and phase. */
export interface Drift {
  x: number;
  y: number;
  dur: number;
  offset: number;
}

/** Ash shed by the runes as they dissolve near the top (2 a rating), tumbling down. */
export function ash(f: PsyFloor, r: number): Drift[] {
  const H = pace(r).H;
  return Array.from({ length: 2 * r }, (_, k) => ({
    x: f.cx + f.rx * Math.cos(k * 2.1) * 0.9,
    y: f.gy - H * (0.62 + 0.25 * frac(k * 0.618)),
    dur: 4 + (k % 4),
    offset: (k * 0.53) % 4,
  }));
}

/** Where a flake of ash is at `t`: down and across in two legs, turning; in, held, out. */
export function ashAt(d: Drift, t: number): { transform: string; opacity: number } {
  const ph = frac((t + d.offset) / d.dur);
  const [dx, dy, turn] =
    ph < 0.5 ? [120 * ph, 440 * ph, 400 * ph] : [60 - 180 * (ph - 0.5), 220 + 480 * (ph - 0.5), 200 + 320 * (ph - 0.5)];
  return {
    transform: `translate(${fixed(d.x + dx)} ${fixed(d.y + dy)}) rotate(${fixed(turn)})`,
    opacity: fixed(ph < 1 / 3 ? 2.7 * ph : ph < 2 / 3 ? 0.9 - 0.9 * (ph - 1 / 3) : 0.6 - 1.8 * (ph - 2 / 3), 2),
  };
}

/** From 3: motes rising up the lit body (4 + the rating), each through its own height. */
export function motes([x0, y0, x1, y1]: Box, r: number): (Loop<'cy' | 'opacity'> & { cx: number })[] {
  if (r < 3) return [];
  const dur = 4 - 0.15 * r;
  return Array.from({ length: 4 + r }, (_, k) => {
    const y = y0 + (y1 - y0) * (0.2 + 0.75 * frac(k * 0.37));
    const offset = (k * 0.45) % dur;
    return {
      cx: x0 + (x1 - x0) * frac(k * 0.618),
      cy: loop('cy', [y, y - 360 - 20 * r], dur, { offset }),
      opacity: loop('opacity', [0, 0.8, 0], dur, { offset }),
    };
  });
}

/** From 8: ten shards lifting inside the ring, turning as they rise. */
export function debris(f: PsyFloor, r: number): (Drift & { size: number; side: number; rise: number; spin: number })[] {
  if (r < 8) return [];
  return Array.from({ length: 10 }, (_, k) => ({
    x: f.cx + f.rx * 0.8 * (frac(k * 0.618) * 2 - 1),
    y: f.gy,
    dur: 5 + (k % 4),
    offset: (k * 0.7) % (5 + (k % 4)),
    size: 14 + (k % 3) * 8,
    side: k % 2 ? 40 : -40,
    rise: 700 + 60 * (k % 5),
    spin: 3 + (k % 3),
  }));
}

/** Where a lifted shard is at `t`: up and aside, turning on its own beat; in, held, out. */
export function shardAt(d: ReturnType<typeof debris>[number], t: number): { transform: string; opacity: number } {
  const ph = frac((t + d.offset) / d.dur);
  const turn = frac((t + d.offset) / d.spin) * 360;
  const x = d.x + d.side * ph;
  return {
    transform: `translate(${fixed(x)} ${fixed(d.y - d.rise * ph)}) rotate(${fixed(turn)})`,
    opacity: fixed(ph < 1 / 3 ? 2.7 * ph : ph < 2 / 3 ? 0.9 : 0.9 - 2.7 * (ph - 2 / 3), 2),
  };
}

/** A point on the floor at distance `d` and angle `a` from the feet, foreshortened like the ring. */
const onFloor = (f: PsyFloor, d: number, a: number) => [f.cx + d * Math.cos(a), f.gy + d * 0.16 * Math.sin(a)];

/** The gravity well: grit (6 + 3 a rating) sliding in to the feet in slow spirals, faster and from further. */
export function grit(f: PsyFloor, r: number): (Loop<'cx' | 'cy' | 'opacity'> & { r: number })[] {
  const R0 = f.rx * (0.6 + 0.12 * r);
  const dur = 6.5 - 0.4 * r;
  return Array.from({ length: 6 + 3 * r }, (_, k) => {
    const path = Array.from({ length: 25 }, (_, i) => onFloor(f, R0 * (1 - i / 24) + 30, k * 2.399 + (i / 24) * 3.2));
    const offset = (k * 0.37) % dur;
    return {
      cx: loop(
        'cx',
        path.map(([x]) => fixed(x)),
        dur,
        { offset },
      ),
      cy: loop(
        'cy',
        path.map(([, y]) => fixed(y)),
        dur,
        { offset },
      ),
      opacity: loop('opacity', [0, 0.8, 0.8, 0], dur, { offset }),
      r: 5 + (k % 3) * 2,
    };
  });
}

/** Shockwave ripples (1 + half the rating) running out across the floor from the feet, fading. */
export function ripples(f: PsyFloor, r: number): Loop<'rx' | 'ry' | 'opacity'>[] {
  const n = 1 + Math.floor(r / 2);
  const R = f.rx * (0.8 + 0.15 * r);
  const dur = 2.8 - 0.15 * r;
  return Array.from({ length: n }, (_, k) => {
    const offset = (k / n) * dur;
    return {
      rx: loop('rx', [80, R], dur, { offset }),
      ry: loop('ry', [80 * 0.16, R * 0.16], dur, { offset }),
      opacity: loop('opacity', [0.8, 0], dur, { offset }),
    };
  });
}
